// FR-AD7 operator console: "Grackle staff only, separate authentication and separate audit
// trail" — see server/src/ops.rs's module doc for what "separate authentication" means here
// (a password step-up on top of the normal session, not a second IdP). This layout is the
// gate: GET /ops/whoami decides between "not staff" (redirect out — the console isn't
// discoverable by looking at it, same as it not being linked from the org app), "staff but
// hasn't stepped up" (password re-entry form), and "staff, stepped up" (the console itself).
//
// Deliberately its own top bar, not the org AppShell — FR-AD7 is explicit that this is "not an
// org-facing feature and not exposed on the public app surface," so it should not look like it
// grew out of the same shell a member sees. Dense and utilitarian: DS Tabs, Cards and tables.
import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Badge, Card, Icon, Tabs, Wordmark } from "@grackle/ui";
import { api, ApiFail } from "../api/client";
import type { OpsWhoamiOut } from "../api/types";
import { Button, Field, Notice, PageLoader } from "../components/ui";
import { useSession } from "../session";
import "../admin/admin.css";
import "./ops.css";

const TABS = [
  { value: "", label: "Overview" },
  { value: "orgs", label: "Orgs" },
  { value: "videos", label: "Videos" },
  { value: "jobs", label: "Jobs" },
  { value: "email", label: "Email" },
  { value: "integrations", label: "Integrations" },
  { value: "notetaker", label: "Notetaker" },
  { value: "media", label: "Media" },
  { value: "tokens", label: "Tokens" },
];

function StepUpForm({ onDone }: { onDone: () => void }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api("POST", "/ops/stepup", { password });
      onDone();
    } catch (err) {
      setError(
        err instanceof ApiFail && err.code === "invalid_credentials"
          ? "Incorrect password."
          : "Couldn't verify that.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="center-page">
      <Card size="lg" title="Confirm it's you" className="ops-stepup">
        <p className="gk-sm">Re-enter your password to open the operator console. This step-up lasts 15 minutes.</p>
        <form onSubmit={onSubmit} className="auth-form">
          <Field
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error ? <Notice tone="error">{error}</Notice> : null}
          <Button type="submit" block loading={submitting} disabled={!password}>
            Continue
          </Button>
        </form>
      </Card>
    </div>
  );
}

export default function OpsLayout() {
  const { user, loading: sessionLoading } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [whoami, setWhoami] = useState<OpsWhoamiOut | null>(null);
  const [loadError, setLoadError] = useState(false);

  const reload = () => {
    api<OpsWhoamiOut>("GET", "/ops/whoami")
      .then(setWhoami)
      .catch(() => setLoadError(true));
  };

  useEffect(() => {
    if (user) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (sessionLoading) return <PageLoader />;
  if (!user) return <Navigate to="/signin" replace />;
  if (loadError) return <div className="center-page">Couldn't reach Grackle.</div>;
  if (!whoami) return <PageLoader />;
  if (!whoami.is_platform_admin) return <Navigate to="/orgs" replace />;
  if (!whoami.stepped_up) return <StepUpForm onDone={reload} />;

  return (
    <div className="ops-shell">
      <header className="ops-bar">
        <Link to="/orgs" className="ops-bar__home" aria-label="Grackle home">
          <Wordmark size={18} />
        </Link>
        <Badge tone="inverse" size="sm">
          OPS
        </Badge>
        <Tabs
          overflow="menu"
          items={TABS}
          value={location.pathname.split("/")[2] ?? ""}
          onChange={(page) => navigate(page ? `/ops/${page}` : "/ops")}
        />
        <Link to="/orgs" className="gk-btn gk-btn--secondary gk-btn--sm">
          <Icon name="arrow-left" size={18} />
          Back to Grackle
        </Link>
      </header>
      <main className="ops-main" key={location.pathname}>
        <Outlet />
      </main>
    </div>
  );
}
